import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATUS_COLOR_VAR, STATUS_I18N_KEY } from './shared.js';
import type { CaseRow } from './shared.js';
import { IDT_STATUS_LABEL_TO_ID, idtKpiApi } from '../../../lib/api.js';
import type { IdtCaseStatus } from '../../../lib/api.js';
import { useUpdateIdtCase } from '../../../hooks/useIdtKpi.js';

/** ドロップダウンに並べるステータス順 (上から)。 @graph-connects none */
const STATUS_ORDER: IdtCaseStatus[] = ['未対応', '処理中', '提出済み', '完了'];

/** ステータス pill / dropdown の統一幅 (短い方の設計に揃える)。 @graph-connects none */
const STATUS_CELL_WIDTH = 'w-[80px]';

/**
 * 実績入力の正規化。日本語/中国語 IME は数字キーで全角数字 (０-９) を入力するため、
 * まず全角数字・全角ドットを半角へ変換してから数字とドット以外を除去する。これをしないと
 * `\d` (半角のみ) に弾かれて「新しい数字が入力できない」状態になる。
 * @graph-connects none
 */
function normalizeNumericInput(value: string): string {
  return value
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[．。]/g, '.')
    .replace(/[^\d.]/g, '')
    .replace(/(\..*)\./g, '$1');
}

/**
 * 行の編集ドロップダウンで選べるステータス。提出済みはデザイン種別のみ
 * (Backlog 上 30503 がデザイン種別専用のため)。現在値が提出済みなら種別問わず含める。
 * @graph-connects none
 */
function statusOptionsFor(row: CaseRow): IdtCaseStatus[] {
  return STATUS_ORDER.filter(
    (s) => s !== '提出済み' || row.category === 'デザイン' || row.status === '提出済み',
  );
}

/**
 * 案件名セル。クリックで母票案件仕様書の「IDpt算出シート」タブを別タブで開く。
 * ポップアップブロック回避のため、クリック同期で空タブを開いてから解決済み URL を流し込む。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当案件明細の案件名セル。クリックで母票 IDpt算出シートを開く
 * @graph-connects idtKpiApi [calls] IDpt算出シート URL 解決
 */
export function CaseNameButton({ row }: { row: CaseRow }) {
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    const win = window.open('about:blank', '_blank');
    setLoading(true);
    idtKpiApi
      .getSpecSheetUrl(row.issueKey)
      .then(({ url }) => {
        if (url && win) win.location.href = url;
        else if (win) win.close();
      })
      .catch(() => {
        if (win) win.close();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      title={row.name}
      className="block w-full text-left truncate font-medium text-[var(--color-text-primary)] hover:text-[var(--color-accent-primary)] hover:underline transition-colors disabled:opacity-60 disabled:cursor-progress"
    >
      {row.name}
    </button>
  );
}

/**
 * 実績セル。静的時は数値のみ (read-only と同じ見た目)。担当者本人 / pmot は
 * クリックで入力欄に切り替わり、blur / Enter で Backlog actualHours へ書き戻す。
 * スピナー無し・数字のみ入力 (IME 全角対応)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当案件明細の実績セル。クリックで非制御 input に切替え actualHours を編集
 * @graph-connects useUpdateIdtCase [calls] actualHours 更新
 */
export function ActualCell({ row, editable }: { row: CaseRow; editable: boolean }) {
  const { t } = useTranslation();
  const mutation = useUpdateIdtCase();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  if (!editable || !editing) {
    return (
      <span
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : undefined}
        onClick={
          editable
            ? () => {
                setEditing(true);
              }
            : undefined
        }
        onKeyDown={
          editable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setEditing(true);
                }
              }
            : undefined
        }
        className={
          editable
            ? 'inline-block px-1 rounded cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors'
            : undefined
        }
        title={editable ? t('idtKpi.columns.actual', '実績') : undefined}
      >
        {row.actual}
      </span>
    );
  }

  // 非制御 input (ref 読み取り)。親が頻繁に再レンダーしても入力値が巻き戻らない。
  // IME 変換中 (isComposing) はサニタイズせず、確定後 (compositionend / input) に正規化する。
  const sanitize = (el: HTMLInputElement) => {
    const cleaned = normalizeNumericInput(el.value);
    if (cleaned !== el.value) el.value = cleaned;
  };
  const commit = () => {
    setEditing(false);
    const next = parseFloat(normalizeNumericInput(inputRef.current?.value ?? ''));
    if (Number.isNaN(next) || next < 0 || next === row.actual) return;
    mutation.mutate({ childIssueKey: row.childIssueKey, actualHours: next });
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      autoFocus
      defaultValue={String(row.actual)}
      onInput={(e) => {
        // IME 変換中は触らない (全角→半角変換や数字キーの取り込みを壊さないため)。
        if (e.nativeEvent.isComposing) return;
        sanitize(e.currentTarget);
      }}
      onCompositionEnd={(e) => {
        sanitize(e.currentTarget);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          e.currentTarget.value = String(row.actual);
          setEditing(false);
        }
      }}
      aria-label={t('idtKpi.columns.actual', '実績')}
      className="w-14 px-1 py-0.5 text-right tabular-nums rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] transition-colors"
    />
  );
}

/**
 * ステータスセル。静的時は read-only と同じ色付き pill。担当者本人 / pmot はクリックで
 * ドロップダウンに切り替わり、選択 / blur で Backlog statusId へ書き戻して静的表示へ戻る。
 * pill と dropdown は同じ固定幅で揃える。提出済みはデザイン種別のみ選択可。完了選択時は
 * onCompleting で親へ退場アニメ開始を通知する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当案件明細のステータスセル。クリックで dropdown に切替え statusId を編集
 * @graph-connects useUpdateIdtCase [calls] statusId 更新
 */
export function StatusCell({
  row,
  editable,
  onCompleting,
}: {
  row: CaseRow;
  editable: boolean;
  /** 完了に変更したとき、退場アニメ開始を親へ通知する */
  onCompleting: (childIssueKey: string) => void;
}) {
  const { t } = useTranslation();
  const mutation = useUpdateIdtCase();
  const [editing, setEditing] = useState(false);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const colorVar = STATUS_COLOR_VAR[row.status];
  const label = t(`idtKpi.status.${STATUS_I18N_KEY[row.status]}`);

  useEffect(() => {
    if (!editing) return;
    const el = selectRef.current as (HTMLSelectElement & { showPicker?: () => void }) | null;
    if (!el) return;
    el.focus();
    // クリック直後のユーザー操作内ならネイティブのドロップダウンを即座に開く。
    try {
      el.showPicker?.();
    } catch {
      // showPicker 非対応 / ジェスチャ外。focus 済みなので手動で開ける。
      el.focus();
    }
  }, [editing]);

  if (!editable || !editing) {
    return (
      <span
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : undefined}
        onClick={
          editable
            ? () => {
                setEditing(true);
              }
            : undefined
        }
        onKeyDown={
          editable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setEditing(true);
                }
              }
            : undefined
        }
        className={`inline-flex items-center justify-center gap-1.5 ${STATUS_CELL_WIDTH} px-1.5 py-0.5 rounded text-[11px] font-medium select-none ${
          editable ? 'cursor-pointer' : ''
        }`}
        style={{
          background: `color-mix(in srgb, var(${colorVar}) 18%, transparent)`,
          color: `var(${colorVar})`,
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: `var(${colorVar})` }} />
        {label}
      </span>
    );
  }

  return (
    <select
      ref={selectRef}
      value={row.status}
      disabled={mutation.isPending}
      aria-label={t('idtKpi.columns.status', '状態')}
      onChange={(e) => {
        const next = e.target.value as IdtCaseStatus;
        setEditing(false);
        if (next === row.status) return;
        // 完了にする場合は先に退場アニメを開始してから楽観更新する。
        if (next === '完了') onCompleting(row.childIssueKey);
        mutation.mutate({
          childIssueKey: row.childIssueKey,
          statusId: IDT_STATUS_LABEL_TO_ID[next],
        });
      }}
      onBlur={() => {
        setEditing(false);
      }}
      className={`${STATUS_CELL_WIDTH} text-center px-1.5 py-0.5 rounded text-[11px] font-medium border appearance-none focus:outline-none focus:border-[var(--color-border-focus)] disabled:opacity-50 transition-colors cursor-pointer`}
      style={{
        background: `color-mix(in srgb, var(${colorVar}) 14%, var(--color-bg-elevated))`,
        color: `var(${colorVar})`,
        borderColor: `color-mix(in srgb, var(${colorVar}) 40%, transparent)`,
      }}
    >
      {statusOptionsFor(row).map((s) => (
        <option
          key={s}
          value={s}
          className="text-[var(--color-text-primary)] bg-[var(--color-bg-surface)]"
        >
          {t(`idtKpi.status.${STATUS_I18N_KEY[s]}`)}
        </option>
      ))}
    </select>
  );
}
